import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { ChevronLeft, MapPin, Users, BarChart3 } from 'lucide-react'
import axios from 'axios'
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { buildApiUrl } from '../../../config/api'
import { Card, Badge, EmptyState, SlidePanel } from '../../../components/ui'

interface Section {
  id: number; nr_zona: number; nr_secao: number; nm_local_votacao: string | null
  ds_endereco: string | null; nm_bairro: string | null
  latitude: number; longitude: number; qt_eleitores: number
  nm_municipio: string; sg_uf: string
  our_voters: number; engagement: number
}

interface Stats {
  total_sections: number; total_tse_voters: number; total_our_voters: number
  sections_with_voters: number; engagement: number
}

interface ScopeData {
  scope: string; state_id: number; state_ibge_id: number; state_code: string
  city_id?: number; city_ibge_id?: number; city_name?: string
}

function getColor(engagement: number, ourVoters: number): string {
  if (ourVoters === 0) return '#94a3b8'
  if (engagement >= 5) return '#15803d'
  if (engagement >= 3) return '#22c55e'
  if (engagement >= 1.5) return '#4ade80'
  if (engagement >= 0.5) return '#3b82f6'
  return '#93c5fd'
}

function getRadius(qtEleitores: number): number {
  if (qtEleitores >= 1000) return 10
  if (qtEleitores >= 500) return 8
  if (qtEleitores >= 200) return 6
  return 5
}

export default function SectionsMapPage() {
  const navigate = useNavigate()
  const [sections, setSections] = useState<Section[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [scope, setScope] = useState<ScopeData | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedSection, setSelectedSection] = useState<Section | null>(null)
  const [panelOpen, setPanelOpen] = useState(false)
  const [geoJson, setGeoJson] = useState<any>(null)
  const [filter, setFilter] = useState<'all' | 'campaign' | 'not_campaign'>('all')
  const [hasCampaign, setHasCampaign] = useState(false)
  const mapFitted = useRef(false)

  useEffect(() => { fetchData() }, [filter])

  async function fetchData() {
    setLoading(true)
    mapFitted.current = false
    try {
      const res = await axios.get(buildApiUrl('/v1/reports/sections/map'), { params: { filter } })
      setSections(res.data.data.sections)
      setStats(res.data.data.stats)
      setScope(res.data.data.scope)
      setHasCampaign(res.data.data.has_campaign)
      if (res.data.data.scope?.state_id) {
        try {
          const geoRes = await axios.get(buildApiUrl(`/v1/geo/states/${res.data.data.scope.state_id}/boundaries`))
          setGeoJson(geoRes.data)
        } catch { /* */ }
      }
    } catch { /* */ } finally { setLoading(false) }
  }

  function openSection(s: Section) {
    setSelectedSection(s)
    setPanelOpen(true)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/reports/map')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mapa de Seções</h1>
            <p className="text-gray-500 mt-1">Seções eleitorais com engajamento da base</p>
          </div>
        </div>
        {hasCampaign && (
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {(['all', 'campaign', 'not_campaign'] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)} className={clsx('px-3 py-1.5 rounded-md text-sm font-medium transition-colors', filter === f ? 'bg-brand-50 text-brand-600 shadow-xs' : 'text-gray-500')}>
                {f === 'all' ? 'Todos' : f === 'campaign' ? 'Campanha' : 'Fora'}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <Card>
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-500 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-500">Carregando seções...</p>
            </div>
          </div>
        </Card>
      ) : !scope ? (
        <Card>
          <EmptyState
            icon={<MapPin className="w-12 h-12" />}
            title="Abrangência incompleta"
            description="Configure a abrangência da organização em Configurações > Político para visualizar o mapa."
          />
        </Card>
      ) : (<>
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {[
              { v: stats.total_sections.toLocaleString('pt-BR'), l: 'Seções' },
              { v: stats.total_tse_voters.toLocaleString('pt-BR'), l: 'Eleitores TSE' },
              { v: stats.total_our_voters.toLocaleString('pt-BR'), l: 'Nossa base' },
              { v: stats.sections_with_voters.toLocaleString('pt-BR'), l: 'Seções com presença' },
              { v: `${stats.engagement}%`, l: 'Engajamento' },
            ].map(s => (
              <div key={s.l} className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-semibold text-gray-900">{s.v}</p>
                <p className="text-xs text-gray-500 mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        )}

        {/* Mapa */}
        <Card padding="none">
          <div style={{ height: 'min(550px, 65vh)' }} className="rounded-2xl overflow-hidden">
            {sections.length > 0 ? (
              <MapContainer
                center={[sections[0].latitude, sections[0].longitude]}
                zoom={scope?.city_id ? 13 : 7}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
                ref={mapRef => {
                  if (mapRef && sections.length > 0 && !mapFitted.current) {
                    mapFitted.current = true
                    setTimeout(() => {
                      try {
                        const bounds = L.latLngBounds(sections.map(s => [s.latitude, s.longitude]))
                        if (bounds.isValid()) mapRef.fitBounds(bounds, { padding: [30, 30] })
                      } catch { /* */ }
                    }, 100)
                  }
                }}
              >
                <TileLayer
                  attribution='&copy; CARTO'
                  url='https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
                />
                {/* Camada 1: todas as seções em cinza */}
                {sections.filter(s => s.our_voters === 0).map(s => (
                  <CircleMarker
                    key={s.id}
                    center={[s.latitude, s.longitude]}
                    radius={getRadius(s.qt_eleitores)}
                    pathOptions={{ fillColor: '#94a3b8', fillOpacity: 0.6, color: '#fff', weight: 0.5 }}
                    eventHandlers={{ click: () => openSection(s) }}
                  >
                    <Tooltip>
                      <strong>Zona {s.nr_zona} — Seção {s.nr_secao}</strong><br />
                      {s.nm_local_votacao && <>{s.nm_local_votacao}<br /></>}
                      {s.qt_eleitores.toLocaleString('pt-BR')} eleitores TSE<br />
                      Sem presença
                    </Tooltip>
                  </CircleMarker>
                ))}
                {/* Camada 2: seções com presença — por cima, destaque */}
                {sections.filter(s => s.our_voters > 0).map(s => (
                  <CircleMarker
                    key={`h-${s.id}`}
                    center={[s.latitude, s.longitude]}
                    radius={getRadius(s.qt_eleitores)}
                    pathOptions={{ fillColor: getColor(s.engagement, s.our_voters), fillOpacity: 1, color: '#fff', weight: 2 }}
                    eventHandlers={{ click: () => openSection(s) }}
                  >
                    <Tooltip>
                      <strong>Zona {s.nr_zona} — Seção {s.nr_secao}</strong><br />
                      {s.nm_local_votacao && <>{s.nm_local_votacao}<br /></>}
                      {s.qt_eleitores.toLocaleString('pt-BR')} eleitores TSE<br />
                      {s.our_voters} da nossa base ({s.engagement}%)
                    </Tooltip>
                  </CircleMarker>
                ))}
              </MapContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <EmptyState icon={<MapPin className="w-8 h-8" />} title="Sem dados" description="Nenhuma seção com coordenadas encontrada." />
              </div>
            )}
          </div>
        </Card>

        {/* Legenda */}
        <div className="flex items-center justify-center gap-4 text-xs text-gray-500 flex-wrap">
          {[['#94a3b8','Sem presença'],['#93c5fd','Baixo'],['#3b82f6','Médio'],['#4ade80','Bom'],['#22c55e','Alto'],['#15803d','Muito alto']].map(([c,l]) => (
            <span key={l} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: c }} />{l}</span>
          ))}
        </div>

        {/* Top seções com mais presença */}
        {sections.filter(s => s.our_voters > 0).length > 0 && (
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Seções com maior presença</h3>
            <div className="space-y-2">
              {sections.filter(s => s.our_voters > 0).sort((a, b) => b.our_voters - a.our_voters).slice(0, 20).map((s, i) => {
                const maxOur = sections.reduce((max, s) => Math.max(max, s.our_voters), 1)
                return (
                  <div key={s.id} className="flex items-center gap-3 p-2 -mx-2 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors" onClick={() => openSection(s)}>
                    <span className="w-6 text-right text-xs font-bold text-gray-400">{i + 1}</span>
                    <span className="w-48 text-left text-sm font-medium text-gray-800 truncate">
                      Zona {s.nr_zona} — Seção {s.nr_secao}
                    </span>
                    <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.max((s.our_voters / maxOur) * 100, 8)}%`, backgroundColor: getColor(s.engagement, s.our_voters) }} />
                    </div>
                    <span className="text-sm font-semibold text-gray-800 w-12 text-right">{s.our_voters}</span>
                    <span className="text-xs text-gray-400 w-16 text-right">{s.engagement}%</span>
                  </div>
                )
              })}
            </div>
          </Card>
        )}
      </>)}

      {/* SlidePanel - Detalhes da seção */}
      <SlidePanel
        open={panelOpen}
        onClose={() => { setPanelOpen(false); setSelectedSection(null) }}
        title={selectedSection ? `Zona ${selectedSection.nr_zona} — Seção ${selectedSection.nr_secao}` : ''}
        subtitle={selectedSection?.nm_municipio}
      >
        {selectedSection && (
          <div className="p-6 space-y-5">
            {/* Local */}
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Local de votação</h4>
              <p className="text-sm font-medium text-gray-900">{selectedSection.nm_local_votacao || '—'}</p>
              {selectedSection.ds_endereco && <p className="text-sm text-gray-500 mt-1">{selectedSection.ds_endereco}</p>}
              {selectedSection.nm_bairro && <p className="text-sm text-gray-500">{selectedSection.nm_bairro}</p>}
            </div>

            {/* Números */}
            <div className="border-t border-gray-100 pt-4">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Eleitores</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-semibold text-gray-900">{selectedSection.qt_eleitores.toLocaleString('pt-BR')}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">TSE</p>
                </div>
                <div className="bg-brand-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-semibold text-brand-600">{selectedSection.our_voters}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">Nossa base</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-semibold text-gray-900">{selectedSection.engagement}%</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">Engajamento</p>
                </div>
              </div>
            </div>

            {/* Barra de engajamento */}
            <div className="border-t border-gray-100 pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Cobertura</span>
                <span className="text-xs font-medium text-gray-700">{selectedSection.our_voters} / {selectedSection.qt_eleitores.toLocaleString('pt-BR')}</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{
                  width: `${Math.min(selectedSection.engagement, 100)}%`,
                  backgroundColor: getColor(selectedSection.engagement, selectedSection.our_voters),
                  minWidth: selectedSection.our_voters > 0 ? '4px' : '0',
                }} />
              </div>
            </div>
          </div>
        )}
      </SlidePanel>
    </div>
  )
}
